"use client";

import OnlineUsers from "@/components/shared/online-users";
import { BASE_URL } from "@/lib/constants";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { IGetVCsResponseDTO } from "@/types/venture-capital";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import { CreateAccountCard } from "../../home/components/sidebar-cards";
import VCCard from "./cards/vc-card";
import VCListHeroSection from "./hero-section";
import VCBreadCrumb from "@/components/common/vc-bread-crumb";
import { ModalType } from "@/providers/modal-provider.types";

const fetchVCs = async (filters: TVCListingFilters & { page: number; perPage: number }) => {
	try {
		const url = new URL(`${BASE_URL}/api/vc`);
		if (filters.page) {
			url.searchParams.set("page", filters.page.toString() || "1");
		}

		if (filters.perPage) {
			url.searchParams.set("perPage", filters.perPage.toString());
		}

		if (filters.hqLocation) {
			url.searchParams.set("location", filters.hqLocation);
		}

		if (filters.investmentStage) {
			url.searchParams.set("investmentStage", filters.investmentStage);
		}
		if (filters.sectorFocusTag) {
			url.searchParams.set("sectorFocusTag", filters.sectorFocusTag);
		}

		if (filters.searchQuery) {
			url.searchParams.set("searchQuery", filters.searchQuery);
		}

		const response = await fetch(url, {
			next: { revalidate: 600 },
		});

		if (!response.ok) {
			return [];
		}

		const data = await response.json();

		return data;
	} catch (error) {
		console.log("Error occured while fetching VCs");

		return [];
	}
};

export type TVCListingFilters = {
	hqLocation?: string;
	investmentStage?: string;
	sectorFocusTag?: string;
	searchQuery?: string;
};

const VCPage = () => {
	const perPage = 30;
	const { isLoggedIn } = useAuth();
	const { openModal } = useModal();

	const [filters, setFilters] = useState<TVCListingFilters>({});

	const {
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
		isLoading = true,
		data: vcsData,
	} = useInfiniteQuery<IGetVCsResponseDTO>({
		queryKey: ["vcs", filters],
		queryFn: ({ pageParam }) => fetchVCs({ ...filters, page: pageParam, perPage }),
		getNextPageParam: (lastPage) =>
			lastPage.pagination?.page ? parseInt(lastPage.pagination?.page) + 1 : undefined,
	});

	const vcs = useMemo(() => {
		const formattedVCsData: IGetVCsResponseDTO["data"] = [];

		vcsData?.pages?.forEach((page) => {
			if (page?.data) {
				page?.data?.forEach((vc) => formattedVCsData.push(vc));
			}
		});

		return formattedVCsData;
	}, [vcsData?.pages]);

	const handleShowMoreVCs = () => {
		if (!isLoggedIn) {
			openModal(ModalType.Auth);
		} else {
			fetchNextPage();
		}
	};

	return (
		<div>
			<VCListHeroSection filters={filters} setFilters={setFilters} />

			<section className="mx-auto flex w-11/12 max-w-7xl flex-col gap-4 py-12 max-md:flex-wrap md:w-full">
				<VCBreadCrumb />

				<div className="flex items-start justify-between gap-8 max-md:flex-wrap">
					<div className="w-full md:w-fit">
						{isLoading ? (
							<Loader2Icon className="h-6 w-6 animate-spin stroke-brand-primary" />
						) : (
							<div className="mx-auto mb-16 grid gap-8 md:mx-0 lg:grid-cols-3">
								{vcs?.map((vc) => (
									<VCCard key={vc.slug} vc={vc} />
								))}
							</div>
						)}

						<div className="mt-8 w-full text-center">
							{!isLoading && !hasNextPage ? (
								<div className="flex items-center justify-center gap-x-1 text-sm font-medium text-gray-400">
									<span>You have reached the end.</span>
									<button
										className="underline"
										onClick={() => window?.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
									>
										back to top
									</button>
								</div>
							) : !isLoading && vcs.length >= perPage ? (
								<button
									onClick={handleShowMoreVCs}
									className="mx-auto block max-h-14 w-fit rounded-lg bg-white px-6 py-3 text-center text-sm font-medium text-gray-900 disabled:opacity-40 md:text-lg"
									disabled={isFetchingNextPage}
								>
									{isFetchingNextPage ? (
										<Loader2Icon className="h-5 w-5 animate-spin stroke-brand-primary" />
									) : (
										"Show more"
									)}
								</button>
							) : null}
						</div>
					</div>

					<aside className="w-fit space-y-5 lg:w-72">
						<OnlineUsers />
						<CreateAccountCard />
					</aside>
				</div>
			</section>
		</div>
	);
};

export default VCPage;
